import React, { useState, useCallback, useEffect } from 'react';
import { useClientAccess } from '../hooks/useClientAccess';
import { useI18n } from '../contexts/I18nContext';
import { HardwareTypesTable } from '../components/deviceTypes/HardwareTypesTable';
import { ModelReferencesTable } from '../components/deviceTypes/ModelReferencesTable';
import { typesService } from '../services/typesService';
import '../styles/config-templates.css';
import '../styles/device-types.css';

type TabType = 'hardware' | 'modelrefs';

/**
 * The product catalogue: hardware types (one per product, with its base
 * configuration) and their model references (hardware revisions).
 */
export const DeviceTypesPage: React.FC = () => {
  const { canEditDeviceTypes } = useClientAccess();
  const { formatNumber, formatTime, t } = useI18n();
  const [activeTab, setActiveTab] = useState<TabType>('hardware');
  const [refreshTypes, setRefreshTypes] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [hardwareCount, setHardwareCount] = useState<number | null>(null);
  const [modelRefsCount, setModelRefsCount] = useState<number | null>(null);

  // Tab counts: the same stats request the table makes, shared through the service.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await typesService.getHardwareTypeStats();
        if (cancelled) return;
        setHardwareCount(res.total_types ?? 0);
        setModelRefsCount(res.total_model_refs ?? 0);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[DEVICE TYPES PAGE] stats fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTypes]);

  const handleRefresh = useCallback(() => {
    typesService.clearCache();
    setRefreshTypes(prev => prev + 1);
    setLastUpdated(new Date());
  }, []);

  return (
    <div className="page device-types-page">
      <div className="page-header">
        <div className="page-title">
          <h1>{t('deviceTypes.title')}</h1>
          <p>{t('deviceTypes.subtitle')}</p>
        </div>
        <div className="page-actions">
          <span className="last-updated">{t('common.updatedAt', { time: formatTime(lastUpdated) })}</span>
          <button type="button" className="refresh-button" onClick={handleRefresh}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      <div className="dt-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'hardware'}
          className={`dt-tab${activeTab === 'hardware' ? ' active' : ''}`}
          data-testid="tab-hardware-types"
          onClick={() => setActiveTab('hardware')}
        >
          {t('deviceTypes.tabHardware')}
          <span className="ud-tab-count">{hardwareCount == null ? '…' : formatNumber(hardwareCount)}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'modelrefs'}
          className={`dt-tab${activeTab === 'modelrefs' ? ' active' : ''}`}
          data-testid="tab-model-references"
          onClick={() => setActiveTab('modelrefs')}
        >
          {t('deviceTypes.tabModelRefs')}
          <span className="ud-tab-count">{modelRefsCount == null ? '…' : formatNumber(modelRefsCount)}</span>
        </button>
      </div>

      <div className="page-content device-types-content">
        {activeTab === 'hardware' && (
          <HardwareTypesTable
            onTypeSelect={() => {}}
            refreshTrigger={refreshTypes}
            canEdit={canEditDeviceTypes}
          />
        )}
        {activeTab === 'modelrefs' && (
          <ModelReferencesTable
            refreshTrigger={refreshTypes}
            canEdit={canEditDeviceTypes}
          />
        )}
      </div>
    </div>
  );
};
